import * as React from 'react'

import { MoreHorizontal, Trash2 } from 'lucide-react'

import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import { DisabledReasonTooltip } from '~/client/src/shared/components/DisabledReasonTooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { NOOP } from '~/client/src/shared/utils/noop'

import { IResourceControlsLabels } from '../types'

interface IProps {
  onDelete: () => void | Promise<void>
  confirmDelete: IResourceControlsLabels['confirmDelete']
  /** Why this row cannot be deleted; the item is greyed out and explains itself. */
  deniedReason?: string
}

function DeleteResourceCell({ onDelete, confirmDelete, deniedReason }: IProps) {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const openDialog = React.useCallback(() => setIsDialogOpen(true), [])
  const closeDialog = React.useCallback(() => setIsDialogOpen(false), [])

  const handleDelete = React.useCallback(async () => {
    setIsDeleting(true)
    try {
      await onDelete()
      setIsDialogOpen(false)
    } catch (error) {
      console.error('Failed to delete:', error)
    } finally {
      setIsDeleting(false)
    }
  }, [onDelete])

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={Localization.translator.actions}
          className="tw:flex tw:size-7 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-md tw:text-muted-foreground tw:outline-none tw:hover:bg-accent tw:hover:text-foreground tw:data-popup-open:bg-accent tw:data-popup-open:text-foreground"
        >
          <MoreHorizontal className="tw:size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* The trigger stays enabled on a denied row on purpose: opening the menu is how
              the user reaches the explanation. */}
          <DisabledReasonTooltip
            role="none"
            className="tw:flex"
            isDenied={!!deniedReason}
            reason={deniedReason ?? ''}
          >
            <DropdownMenuItem
              variant="destructive"
              disabled={!!deniedReason}
              onClick={openDialog}
            >
              <Trash2 />
              {Localization.translator.delete}
            </DropdownMenuItem>
          </DisabledReasonTooltip>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDialog
        useRedConfirmationButton
        title={Localization.translator.deleteResource}
        isOpen={isDialogOpen}
        loading={isDeleting}
        onCancelClicked={isDeleting ? NOOP : closeDialog}
        onDoneClicked={handleDelete}
        doneTitle={Localization.translator.delete}
      >
        <div className="tw:pb-2.5 tw:text-body-md tw:whitespace-pre-line">
          {confirmDelete(1)}
        </div>
      </ConfirmDialog>
    </>
  )
}

export default DeleteResourceCell
